import React, { useEffect, useState } from "react";
import axios from "axios";
import { useAuth } from "../../../../../../firebase/AuthContext";
import { getApiUrl } from "../../../../../../config/api";
import VideoChapters from "./VideoChapters";

function VideoLibraryRow({ video, language, added, formatDate, onStatusChange, onDelete }) {
    const { currentUser } = useAuth();
    const [expanded, setExpanded] = useState(false);
    const [opened, setOpened] = useState(false);
    const [choice, setChoice] = useState(video.status === "needs_review" ? "" : video.status);
    const [saving, setSaving] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [error, setError] = useState("");
    const t = (en, zh) => (language === "zh" ? zh : en);
    const titleOnly = video.content_scope === "title_only" && video.chunk_count === 0;
    const busy = saving || deleting;
    useEffect(() => { setChoice(video.status === "needs_review" ? "" : video.status); }, [video.status]);

    const saveStatus = async () => {
        setSaving(true);
        setError("");
        try {
            const token = await currentUser.getIdToken();
            const { data } = await axios.patch(`${getApiUrl()}/api/video/${video.video_id}/status`, {
                status: choice, expected_status: video.status,
            }, { headers: { Authorization: `Bearer ${token}` }, timeout: 30_000 });
            if (data?.result !== "updated" || data.video_id !== video.video_id || data.status !== choice ||
                !Number.isInteger(data.chunk_count)) throw new Error("Unconfirmed status change");
            onStatusChange(data);
        } catch (err) {
            setError(err.response?.data?.error || t("Could not confirm the change. Refresh before retrying.", "無法確認修改結果，請先重新整理再重試。"));
        } finally {
            setSaving(false);
        }
    };

    const deleteVideo = async () => {
        if (busy || !window.confirm(t(
            `Delete “${video.title}” from the video library? Its searchable content and chapters will be removed. You can add it again later. The YouTube video will remain unchanged.`,
            `確定從影片庫刪除「${video.title}」？此影片將無法搜尋，章節也會移除。日後可重新加入，YouTube 原影片不受影響。`,
        ))) return;
        setDeleting(true);
        setError("");
        try {
            const token = await currentUser.getIdToken();
            const { data } = await axios.delete(`${getApiUrl()}/api/video/${video.video_id}`, {
                data: { expected_status: video.status, expected_indexed_at: video.indexed_at },
                headers: { Authorization: `Bearer ${token}` }, timeout: 30_000,
            });
            if (data?.result !== "deleted" || data.video_id !== video.video_id ||
                typeof data.changed !== "boolean" || !Number.isInteger(data.chunk_count) || data.chunk_count < 0 ||
                (!data.changed && data.chunk_count !== 0)) throw new Error("Unconfirmed deletion");
            onDelete(video.video_id);
        } catch (err) {
            setError(err.response?.data?.error || t("Deletion could not be confirmed. Refresh before retrying.", "無法確認刪除結果，請先重新整理再重試。"));
            setDeleting(false);
        }
    };

    return <>
        <tr className={added ? "bg-orange-50" : ""}>
            <td className="px-5 py-4 min-w-[240px]">
                <a href={`https://www.youtube.com/watch?v=${encodeURIComponent(video.video_id)}`} target="_blank"
                    rel="noopener noreferrer" className="font-medium text-gray-800 hover:text-orange-600">{video.title}</a>
                <p className="text-xs text-gray-400 mt-1">YouTube ID: {video.video_id}</p>
                {added && <p className="text-xs font-medium text-orange-600">{t("Added this session", "本次新增")}</p>}
                {!titleOnly && <button type="button" aria-expanded={expanded} aria-controls={`chapters-${video.video_id}`}
                    aria-label={`${expanded ? t("Hide chapters for", "收合章節：") : t("Show chapters for", "展開章節：")} ${video.title}`}
                    onClick={() => { setOpened(true); setExpanded((value) => !value); }}
                    className="text-xs text-orange-600 font-medium mt-2">
                    {expanded ? t("Hide chapters", "收合章節") : t("Show chapters", "展開章節")} ({video.chunk_count})
                </button>}
                {titleOnly && <p className="text-xs text-gray-500 mt-2">{t("Title only · no transcript or chapters", "僅標題索引・無逐字稿與章節")}</p>}
            </td>
            <td className="px-3 py-4 text-gray-600">{
                ({ en: t("English", "英文"), zh: t("Chinese", "中文"), tw: t("Chinese", "中文"),
                    ja: t("Japanese", "日文"), jp: t("Japanese", "日文") })[video.language] || video.language
            }</td>
            <td className="px-3 py-4 min-w-[185px]">
                {video.status === "active" && (video.chunk_count > 0 || titleOnly) ?
                    <span className="text-green-700">{t("Searchable", "可搜尋")}</span> :
                    video.status === "needs_review" ? <span className="text-yellow-700">{t("Needs review", "待處理")}</span> :
                        video.status === "inactive" ? <span className="text-gray-500">{t("Disabled", "已停用")}</span> :
                            <span className="text-gray-500">{t("Unavailable", "目前無法搜尋")}</span>}
                <div className="flex items-center gap-2 mt-2">
                    <select aria-label={`${t("Status for", "狀態：")} ${video.title}`} value={choice}
                        onChange={(event) => { setChoice(event.target.value); setError(""); }} disabled={busy || (video.chunk_count === 0 && !titleOnly)}
                        className="text-xs rounded border border-gray-200 p-1.5 disabled:opacity-50">
                        {video.status === "needs_review" && <option value="" disabled>{t("Choose status", "選擇狀態")}</option>}
                        <option value="active">{t("Enabled", "啟用")}</option>
                        <option value="inactive">{t("Disabled", "停用")}</option>
                    </select>
                    <button type="button" onClick={saveStatus} disabled={busy || !choice || choice === video.status || (video.chunk_count === 0 && !titleOnly)}
                        aria-label={`${t("Save status for", "儲存狀態：")} ${video.title}`}
                        className="text-xs font-semibold text-orange-600 disabled:opacity-40">{saving ? t("Saving…", "儲存中…") : t("Save", "儲存")}</button>
                </div>
                {video.chunk_count === 0 && !titleOnly && <p className="text-xs text-gray-500 mt-2 max-w-[200px]">
                    {t("Transcript and indexing required before enabling.", "需先補齊字幕與索引才能啟用。")}
                </p>}
                {error && <p role="alert" className="text-xs text-red-600 mt-2 max-w-[230px]">{error}</p>}
            </td>
            <td className="px-5 py-4 text-xs text-gray-500 whitespace-nowrap">{formatDate(video.indexed_at)}</td>
            <td className="px-5 py-4">
                <button type="button" onClick={deleteVideo} disabled={busy}
                    aria-label={`${t("Delete video", "刪除影片：")} ${video.title}`}
                    className="text-xs font-semibold text-red-600 disabled:opacity-40">
                    {deleting ? t("Deleting…", "刪除中…") : t("Delete", "刪除")}
                </button>
            </td>
        </tr>
        {opened && <tr hidden={!expanded} id={`chapters-${video.video_id}`}>
            <td colSpan={5} className="p-5 bg-gray-50"><VideoChapters videoId={video.video_id} language={language} /></td>
        </tr>}
    </>;
}

export default VideoLibraryRow;
